import {
  AddressChoiceList,
  AddressForm,
  useDefaultAddressStep
} from "@/components/addresses";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, Button } from "@medusajs/ui";
import { useCallback, useRef } from "react";

export interface ExternalCheckoutAddressDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Open-state change callback. Closing without saving aborts the checkout. */
  onOpenChange: (open: boolean) => void;
  /**
   * Called once the address is saved as the default shipping address. The
   * caller closes this dialog and opens the external-checkout interstitial; it
   * must not redirect from here, as the save is async and the click gesture a
   * popup needs is long gone.
   */
  onSaved: () => void;
}

/**
 * Asks for the delivery address a buyer has never set, just before they are
 * sent to a supplier's own checkout (the Shopify prefill reads the default
 * shipping address, so it has to exist before the checkout URL is requested).
 *
 * Two shapes, by what the buyer already has, as on the onboarding page
 * (`useDefaultAddressStep`):
 * - **No saved address**: the address form.
 * - **Saved addresses, none the default**: pick one rather than typing it again.
 *
 * Either way the address ends up as the default shipping and billing address.
 */
export function ExternalCheckoutAddressDialog({
  open,
  onOpenChange,
  onSaved
}: ExternalCheckoutAddressDialogProps) {
  // Set when the dialog is about to close because the address was saved.
  const closingAfterSave = useRef(false);

  const handleSaved = useCallback(() => {
    closingAfterSave.current = true;
    onSaved();
  }, [onSaved]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
        onCloseAutoFocus={(event) => {
          // After a save the caller closes this dialog and opens the
          // interstitial in the same render. Radix would hand focus back to the
          // Checkout button behind the new modal as this one unmounts; leave it
          // to the interstitial's own focus handling. A cancel still returns
          // focus to the trigger as usual.
          if (closingAfterSave.current) {
            closingAfterSave.current = false;
            event.preventDefault();
          }
        }}
      >
        {/* Mounted only while open, so each opening starts from a clean form. */}
        <AddressStep
          onSaved={handleSaved}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function AddressStep({
  onSaved,
  onCancel
}: {
  onSaved: () => void;
  onCancel: () => void;
}) {
  const {
    isLoading,
    addressesError,
    retryAddresses,
    addresses,
    hasAddresses,
    showForm,
    defaultValues,
    chosenId,
    setPickedId,
    serverError,
    isCreating,
    isUpdating,
    handleCreate,
    handleChoose,
    startAdding,
    stopAdding
  } = useDefaultAddressStep({ boundary: "external checkout", onSaved });

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {showForm && hasAddresses
            ? "Add a new address"
            : "Where should we deliver?"}
        </DialogTitle>
        <DialogDescription>
          {hasAddresses && !showForm
            ? "Choose the address we should use to prefill the supplier's checkout. You can change it any time in your account."
            : "We'll prefill the supplier's checkout with this address. You can change it any time in your account."}
        </DialogDescription>
      </DialogHeader>
      {isLoading ? (
        <div
          className="flex flex-col gap-y-4"
          role="status"
          aria-label="Loading your details"
        >
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      ) : addressesError ? (
        // Not "no addresses": an unreadable list must not lead to the empty
        // form, which could save a duplicate of an address the buyer has.
        <div className="flex flex-col gap-y-4">
          <Alert className="bg-ui-bg-base items-center p-2" variant="error">
            We couldn&apos;t load your addresses. Please try again.
          </Alert>
          <div className="flex flex-col gap-y-2">
            <Button className="w-full" onClick={() => retryAddresses()}>
              Try again
            </Button>
            <Button className="w-full" variant="transparent" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      ) : showForm ? (
        <AddressForm
          defaultValues={defaultValues}
          showAddressName={hasAddresses}
          onSubmit={handleCreate}
          // Cancelling abandons the checkout; with saved addresses it instead
          // goes back to choosing one.
          onCancel={hasAddresses ? stopAdding : onCancel}
          isSubmitting={isCreating}
          serverError={serverError}
          submitLabel="Save and continue"
        />
      ) : (
        <div className="flex flex-col gap-y-4">
          <AddressChoiceList
            addresses={addresses}
            value={chosenId ?? ""}
            onValueChange={setPickedId}
            disabled={isUpdating}
          />
          {serverError && (
            <Alert className="bg-ui-bg-base items-center p-2" variant="error">
              {serverError}
            </Alert>
          )}
          <div className="flex flex-col gap-y-2">
            <Button
              className="w-full"
              onClick={handleChoose}
              isLoading={isUpdating}
              disabled={!chosenId}
            >
              Use this address
            </Button>
            <Button
              className="w-full"
              variant="secondary"
              onClick={startAdding}
              disabled={isUpdating}
            >
              Add a new address
            </Button>
            <Button
              className="w-full"
              variant="transparent"
              onClick={onCancel}
              disabled={isUpdating}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
